import { inject, Signal } from '@angular/core';

import {
  Mutation,
  MutationStatus,
  QueryClient,
  QueryFilters,
  injectMutation,
  injectMutationState,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { Store } from '@ngrx/store';
import { User } from 'src/app/core/models/core.models';
import { editUserSuccess } from 'src/app/admin/actions/admin-users.actions';
import {
  ManagedUserData,
  UserApiService,
  SendPlatformInvitationResponse,
  UserCompanyMoveReport,
  UserCompanyMoveRunStatus,
} from '../services/user-api.service';
import { UserService } from 'src/app/core/services/user.service';
import { TabSessionService } from 'src/app/core/services/tab-session.service';
import {
  DataObject,
  RecentSearch,
  RecentSearchPayload,
} from 'src/app/core/models/core.models';
import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';

import { firstValueFrom } from 'rxjs';

export function injectAvailableFeedTypesQuery(userId: string) {
  const userApi = inject(UserApiService);

  return injectQuery(() => ({
    queryKey: ['user', userId, 'accessible_feed_types'],
    queryFn: () => firstValueFrom(userApi.getAvailableFeedTypes(userId)),
    staleTime: 1000 * 3,
    retry: 3,
    // retryDelay: (attempt) => 2000 * 2 ** attempt,
  }));
}

export function injectActivatedUserCompaniesQuery(
  query: Signal<string>,
  showAll: Signal<boolean>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => ({
    queryKey: [
      'user',
      'activated_user_companies',
      { query: query(), showAll: showAll() },
    ],
    queryFn: () =>
      firstValueFrom(userApi.getActivatedUserCompanies(query(), showAll())),
    staleTime: 60 * 1000,
  }));
}

/**
 * The request takes no parameters - the backend reads the signed-in user and its
 * active company (`user_recent_objects` is keyed on `active_company_id`). Both
 * are in the query key anyway: changing a key refetches live observers, clearing
 * a cache entry does not.
 */
export interface RecentObjectsScope {
  userId: string;
  companyId: string;
}

/** Mirrors GET /user/get_recent_objects. */
export const recentObjectsQueryKey = ({
  userId,
  companyId,
}: RecentObjectsScope) =>
  ['user', 'recent_objects', { userId, companyId }] as const;

/**
 * Every cached recent-objects list. A `predicate` rather than a `['user']`
 * prefix, which would also sweep the workspace and activated-companies queries.
 */
export const recentObjectsQueriesFilter = (): QueryFilters => ({
  predicate: (query) => query.queryKey[1] === 'recent_objects',
});

/**
 * The user's recently visited objects.
 *
 * `staleTime` and `gcTime` are both `Infinity` deliberately: the quick-search
 * "remove" has no server call at all (no such endpoint exists), so any refetch
 * or GC resurrects a removed row - and Presentation Mode unmounts the header,
 * the only always-mounted observer. The list refreshes only on an explicit
 * trigger: a company switch (a key change) or the company-objects toggle's
 * explicit invalidation (`dropdown-user`).
 */
export function injectRecentObjectsQuery(
  scope: Signal<RecentObjectsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? recentObjectsQueryKey(current)
        : (['user', 'recent_objects', null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getRecentObjects()).then((objects) =>
          (objects ?? []).map(
            (object) => augmentObjectWithIconAndImage(object) as DataObject,
          ),
        ),
      enabled: !!current,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

const RECENT_SEARCHES_ROOT_KEY = ['user', 'recent_searches'] as const;

export interface RecentSearchesScope {
  userId: string;
  companyId: string;
}

export const recentSearchesQueryKey = ({
  userId,
  companyId,
}: RecentSearchesScope) =>
  [...RECENT_SEARCHES_ROOT_KEY, { userId, companyId }] as const;

/**
 * The identity of a recent search. The backend answers both writes with
 * `{ success: true }` and no row, so there is no id to match on and the client
 * dedupes on the criteria themselves.
 */
const recentSearchKey = (search: RecentSearch | RecentSearchPayload) =>
  `${search.query_text}|${JSON.stringify(search.filters ?? {})}|${search.lang ?? ''}|${search.scope_id ?? ''}`;

/**
 * The user's recent searches - the chips under the quick search. No `staleTime`
 * override, unlike recent objects above: removal here is a real
 * `POST /user/remove_recent_search`, so a mount refetch agrees with both writes.
 */
export function injectRecentSearchesQuery(
  scope: Signal<RecentSearchesScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? recentSearchesQueryKey(current)
        : ([...RECENT_SEARCHES_ROOT_KEY, null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getRecentSearches()).then((searches) =>
          // The backend has been seen to answer with a numeric `query_text`.
          (searches ?? []).map((search) => ({
            ...search,
            query_text: String(search.query_text ?? ''),
          })),
        ),
    };
  });
}

/**
 * Record a search (POST /user/add_recent_search). Patches the cache rather than
 * invalidating: the response carries no row to reconcile against, and an extra
 * GET on every executed search would sit on the quick-search hot path.
 */
export function injectAddRecentSearchMutation() {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (search: RecentSearchPayload) =>
      firstValueFrom(userApi.addRecentSearch(search)),
    onSuccess: (_result, search) => {
      const added: RecentSearch = {
        query_text: String(search.query_text ?? ''),
        filters: search.filters ?? null,
        lang: search.lang ?? null,
        scope_id: search.scope_id ?? null,
        result_count: search.result_count ?? null,
        time_modified: Math.floor(Date.now() / 1000),
      };
      const addedKey = recentSearchKey(added);

      patchRecentSearches(queryClient, (searches) =>
        [
          added,
          ...searches.filter((s) => recentSearchKey(s) !== addedKey),
        ].slice(0, 20),
      );
    },
  }));
}

/** Forget a search (POST /user/remove_recent_search), matched on its criteria. */
export function injectRemoveRecentSearchMutation() {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (search: RecentSearchPayload) =>
      firstValueFrom(userApi.removeRecentSearch(search)),
    onSuccess: (_result, search) => {
      const removedKey = recentSearchKey(search);

      patchRecentSearches(queryClient, (searches) =>
        searches.filter((s) => recentSearchKey(s) !== removedKey),
      );
    },
  }));
}

const patchRecentSearches = (
  queryClient: QueryClient,
  patch: (searches: RecentSearch[]) => RecentSearch[],
): void => {
  queryClient.setQueriesData<RecentSearch[]>(
    { queryKey: RECENT_SEARCHES_ROOT_KEY },
    (current) => (current ? patch(current) : current),
  );
};

// ─── User writes the admin Users grid mirrors ─────────────────────────────
// That grid is SSRM - its rows live in ag-grid's block cache, not in the query
// cache - so it cannot be invalidated. Instead it observes these two writes in
// the mutation cache (`injectUserWriteStates`) and mirrors them onto its rows:
// spinner while a row saves, row patch with the response. The mutationKeys are
// what make the writes observable; keep them.

export const MANAGE_USER_MUTATION_KEY = ['user', 'manage'] as const;
export const SEND_INVITATION_MUTATION_KEY = [
  'user',
  'send-invitation',
] as const;
export const MOVE_USER_COMPANY_MUTATION_KEY = [
  'user',
  'move_company_execute',
] as const;

/** Send a platform invitation (POST /user/send_platform_invitation). */
export function injectSendInvitationMutation() {
  const userApi = inject(UserApiService);

  return injectMutation(() => ({
    mutationKey: SEND_INVITATION_MUTATION_KEY,
    mutationFn: (userId: string) =>
      firstValueFrom(userApi.sendPlatformInvitation(userId)),
  }));
}

/** One user write: the editor payload and which of the two endpoints it goes to. */
export interface ManageUserVariables {
  userData: ManagedUserData;
  isCreate: boolean;
}

/**
 * Create (PUT /user/create) or update (POST /user/update) a user - the caller says
 * which of the two it is, the backend guards them differently (SEN-5296).
 *
 * `editUserSuccess` has one consumer left, `auth.reducer`, which merges a
 * self-edit into the signed-in user - client state, so the broadcast stays and
 * lives here, on the write's owner.
 */
export function injectManageUserMutation() {
  const userApi = inject(UserApiService);
  const store = inject(Store);
  return injectMutation(() => ({
    mutationKey: MANAGE_USER_MUTATION_KEY,
    mutationFn: ({ userData, isCreate }: ManageUserVariables) =>
      firstValueFrom(
        isCreate ? userApi.createUser(userData) : userApi.updateUser(userData),
      ),
    onSuccess: (user) => store.dispatch(editUserSuccess({ userData: user })),
  }));
}

/** One user write as the admin Users grid sees it. */
export interface UserWriteState {
  mutationId: number;
  /** 0 until the write actually started; the grid uses it to skip history. */
  submittedAt: number;
  status: MutationStatus;
  /** Undefined while a *create* is in flight - there is no row to point at yet. */
  userId: string | undefined;
  /** The saved / re-invited user, once the write succeeded. */
  user: User | undefined;
}

/**
 * Live state of every manage-user and send-invitation write in the mutation
 * cache, normalised to the row-level facts the grid needs. A signal, so the grid
 * reacts from an `effect`; entries linger for the cache's gcTime, which is why
 * each carries `mutationId` + `submittedAt` for de-duplication.
 */
export function injectUserWriteStates(): Signal<UserWriteState[]> {
  const isInvitation = (mutation: Mutation) =>
    mutation.options.mutationKey?.[1] === SEND_INVITATION_MUTATION_KEY[1];
  const isManage = (mutation: Mutation) =>
    mutation.options.mutationKey?.[1] === MANAGE_USER_MUTATION_KEY[1];
  const isMove = (mutation: Mutation) =>
    mutation.options.mutationKey?.[1] === MOVE_USER_COMPANY_MUTATION_KEY[1];

  return injectMutationState<UserWriteState>(() => ({
    filters: {
      predicate: (mutation) =>
        mutation.options.mutationKey?.[0] === MANAGE_USER_MUTATION_KEY[0] &&
        (isManage(mutation) || isInvitation(mutation) || isMove(mutation)),
    },
    select: (mutation): UserWriteState => {
      const invitation = isInvitation(mutation);
      const move = isMove(mutation);
      const variables = mutation.state.variables as
        | string
        | ManageUserVariables
        | ExecuteUserCompanyMoveVariables
        | undefined;
      const data = mutation.state.data as
        | User
        | SendPlatformInvitationResponse
        | UserCompanyMoveOutcome
        | undefined;

      let userId: string | undefined;
      let user: User | undefined;
      if (invitation) {
        userId = variables as string | undefined;
        user = (data as SendPlatformInvitationResponse | undefined)?.user;
      } else if (move) {
        userId = (variables as ExecuteUserCompanyMoveVariables | undefined)
          ?.userId;
        user = (data as UserCompanyMoveOutcome | undefined)?.user;
      } else {
        userId = (variables as ManageUserVariables | undefined)?.userData.id;
        user = data as User | undefined;
      }

      return {
        mutationId: mutation.mutationId,
        submittedAt: mutation.state.submittedAt,
        status: mutation.state.status,
        userId,
        user,
      };
    },
  }));
}

// ─── Move a user to another company (WAP-1702) ─────────────────────────────

export interface PlanUserCompanyMoveVariables {
  userId: string;
  targetCompanyId: string;
}

export interface ExecuteUserCompanyMoveVariables {
  userId: string;
  runId: string;
  newOwnerId?: string;
}

/** What an executed move resolves with: the report and the re-read user. */
export interface UserCompanyMoveOutcome {
  report: UserCompanyMoveReport;
  user: User;
}

/** The server's i18n'd message when there is one, else the fallback. */
function serverErrorMessage(error: unknown, fallback: string): string {
  const serverMessage = (error as { error?: { message?: unknown } } | null)
    ?.error?.message;
  return typeof serverMessage === 'string' && serverMessage.length > 0
    ? serverMessage
    : fallback;
}

/**
 * Plan a company move (POST /user/move_company_plan). The report describes a
 * pending run the dialog then executes or cancels; nothing is written yet, so
 * there is nothing to invalidate.
 */
export function injectPlanUserCompanyMoveMutation() {
  const userApi = inject(UserApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (variables: PlanUserCompanyMoveVariables) =>
      firstValueFrom(
        userApi.planCompanyMove(variables.userId, variables.targetCompanyId),
      ),
    onError: (error) =>
      toast.showError(
        serverErrorMessage(
          error,
          translate.instant('move-user-company.toast.planning-failed'),
        ),
      ),
  }));
}

/**
 * Execute a planned move (POST /user/move_company_execute), then re-read the
 * user: the outcome carries the moved user so the admin Users grid mirrors the
 * row from the mutation cache exactly like a manage-user write (see
 * `injectUserWriteStates`), and the editor repaints without another request.
 */
export function injectExecuteUserCompanyMoveMutation() {
  const userApi = inject(UserApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationKey: MOVE_USER_COMPANY_MUTATION_KEY,
    mutationFn: async (
      variables: ExecuteUserCompanyMoveVariables,
    ): Promise<UserCompanyMoveOutcome> => {
      const report = await firstValueFrom(
        userApi.executeCompanyMove({
          runId: variables.runId,
          newOwnerId: variables.newOwnerId,
        }),
      );
      const user = await firstValueFrom(userApi.getUser(variables.userId));
      return { report, user };
    },
    onSuccess: ({ report }) => {
      if (report.run_status_id === UserCompanyMoveRunStatus.Executed) {
        toast.showSuccess(
          translate.instant('move-user-company.toast.user-moved'),
        );
      } else {
        toast.showError(
          translate.instant('move-user-company.toast.move-failed'),
        );
      }
    },
    onError: (error) =>
      toast.showError(
        serverErrorMessage(
          error,
          translate.instant('move-user-company.toast.move-failed'),
        ),
      ),
  }));
}

/**
 * Switch the signed-in user's active company (POST /user/switch_company).
 *
 * Ordering matters: `clearCaches()` runs before the request (those nomenclature
 * `Map`s sit outside the query cache), and the per-tab company write must land
 * before callers broadcast `switchUserCompanySuccess` - the interceptor reads it
 * for the company header, so it has to be current before every company-scoped
 * query re-keys. The query cache itself is cleared by
 * `clearQueryCacheOnSessionChange$`, off the `switchUserCompany` trigger.
 */
export function injectSwitchCompanyMutation() {
  const userApi = inject(UserApiService);
  const userService = inject(UserService);
  const tabSession = inject(TabSessionService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (companyId: string) => {
      userService.clearCaches();
      return firstValueFrom(userApi.switchCompany(companyId));
    },
    onSuccess: (_user, companyId) => {
      tabSession.setActiveCompanyId(companyId);
      toast.showSuccess(
        translate.instant('msg.company-switch.company-switched-successfully'),
      );
    },
  }));
}

/**
 * Persist the user's interface language (POST /user/language). No toast - a
 * failed save leaves the language switched for the session and unsaved. Callers
 * broadcast `setCoreLanguageSuccess` once the request lands; that is the cue
 * `HelpContentService` clears its content cache on.
 */
export function injectSetUserLanguageMutation() {
  const userApi = inject(UserApiService);

  return injectMutation(() => ({
    mutationFn: (languageId: number) =>
      firstValueFrom(userApi.setUserLanguage(languageId)),
  }));
}

/**
 * Change a password (POST /user/:id/change_password) - the signed-in user's own
 * or, from the admin user list, someone else's.
 */
export function injectChangePasswordMutation() {
  const userApi = inject(UserApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      userId,
      currentPassword,
      newPassword,
      confirmPassword,
    }: ChangePasswordVariables) =>
      firstValueFrom(
        userApi.changePassword(
          userId,
          currentPassword,
          newPassword,
          confirmPassword,
        ),
      ),
    onSuccess: () =>
      toast.showSuccess(translate.instant('msg.password.change.success')),
    onError: () =>
      toast.showError(translate.instant('msg.password.change.failed')),
  }));
}

export interface ChangePasswordVariables {
  userId: string;
  currentPassword: string | null;
  newPassword: string | null;
  confirmPassword: string | null;
}

/** Bulk user import (POST /user/import_csv). */
export function injectImportUsersMutation() {
  const userApi = inject(UserApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ companyId, file, invite }: ImportUsersVariables) =>
      firstValueFrom(userApi.importUsers(companyId, file, invite)),
    onSuccess: ({ success, successCount, failureCount }) => {
      if (success && failureCount === 0) {
        toast.showSuccess(
          translate.instant('import-users.toast.success', {
            count: successCount,
          }),
        );
        return;
      }

      if (successCount > 0 && failureCount > 0) {
        toast.showWarning(
          translate.instant('import-users.toast.partial', {
            successCount,
            failureCount,
          }),
        );
        return;
      }

      toast.showError(
        translate.instant('import-users.toast.failed', { failureCount }),
      );
    },
    // The server refuses an inactive company with its own message (WAP-1701).
    onError: (error) =>
      toast.showError(
        serverErrorMessage(
          error,
          translate.instant('import-users.toast.request-error'),
        ),
      ),
  }));
}

export interface ImportUsersVariables {
  companyId: string;
  file: File;
  invite: boolean;
}

/**
 * "View all company objects" (POST /user/list_company_objects). The caller
 * (`dropdown-user`) broadcasts `listCompanyObjectsSuccess` with the flag the
 * **server** echoed - not the one that was clicked - and invalidates the
 * workspace and recent-object lists. (Not in `onSuccess` here: those services
 * import this file, so injecting them back would be an import cycle.)
 */
export function injectListCompanyObjectsMutation() {
  const userApi = inject(UserApiService);

  return injectMutation(() => ({
    mutationFn: (listCompanyObjects: boolean) =>
      firstValueFrom(userApi.listCompanyObjects(listCompanyObjects)),
  }));
}

/** Forgotten-password email (POST /user/send_forgotten_password_email). */
export function injectSendForgottenPasswordEmailMutation() {
  const userApi = inject(UserApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ email, resetType }: { email: string; resetType: number }) =>
      firstValueFrom(userApi.sendForgottenPasswordEmail(email, resetType)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant('user-popup.msg.reset-user-password-email-sent'),
      ),
    onError: () =>
      toast.showError(
        translate.instant(
          'user-popup.msg.sending-reset-user-password-email-failed',
        ),
      ),
  }));
}
